'use client';

import React, { useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { Button } from './button';
import { authModel, GoogleUserInfo } from '@/models/authModel';

interface GoogleSignInButtonProps {
  className?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  onError?: (error: string) => void;
  onSuccess?: (user: GoogleUserInfo) => void;
  onLoggedInClick?: (user: GoogleUserInfo) => void;
  loading?: boolean;
  disabled?: boolean;
  mode?: 'login' | 'register';
  user?: GoogleUserInfo | null;
}

export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  className,
  variant = 'outline',
  size = 'md',
  onError,
  onSuccess,
  onLoggedInClick,
  loading = false,
  disabled = false,
  mode = 'login',
  user,
}) => {
  const t = useTranslations('Login');
  const locale = useLocale();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleSignIn = async (_e: React.FormEvent) => {
    if (mode === 'register' && user) {
      // In register mode, if user is already signed in, trigger the logged in handler
      onLoggedInClick?.(user);
      return;
    }

    try {
      setIsLoading(true);

      if (mode === 'register') {
        // For registration, use popup OAuth and get user info
        const userInfo = await authModel.getGoogleUserInfo(locale);
        onSuccess?.(userInfo);
      } else {
        // For login, proceed with normal sign in flow.
        // `redirect` only exists when the user was bounced here from a guarded
        // page; send the locale separately so a sign-in from a bare
        // `/ja-JP/login` still comes back on ja-JP rather than en-AU.
        const redirectPath = searchParams.get('redirect');
        await authModel.signInWithGoogle(redirectPath || undefined, locale);
        // OAuth flow will redirect the user, so we don't call onSuccess here
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to sign in with Google';
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // If in register mode and user is signed in, show user info
  if (mode === 'register' && user) {
    return (
      <Button
        type="button"
        variant="primary"
        size={size}
        className={className}
        onClick={handleGoogleSignIn}
        disabled={disabled}
      >
        <div className="flex items-center">
          {user.avatar && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={user.avatar}
              alt={user.name}
              width={16}
              height={16}
              className="mr-2 h-4 w-4 rounded-full"
            />
          )}
          <span>{t('continueAs', { name: user.name })}</span>
        </div>
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={[
        className,
        variant === 'outline' &&
          'bg-card text-card-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleGoogleSignIn}
      loading={loading || isLoading}
      disabled={disabled}
    >
      <svg
        className="mr-2 h-4 w-4"
        viewBox="0 0 24 24"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
      </svg>
      {t('continueWithGoogle')}
    </Button>
  );
};
